import type { User } from '../types/auth';

/**
 * Rating field names on User that correspond to variant + time control combos
 */
export type RatingFieldName =
  | 'ratingBullet'
  | 'ratingBlitz'
  | 'ratingRapid'
  | 'ratingStandard'
  | 'ratingBulletItalian'
  | 'ratingBlitzItalian'
  | 'ratingRapidItalian'
  | 'ratingStandardItalian'
  | 'ratingBulletEnglish'
  | 'ratingBlitzEnglish'
  | 'ratingRapidEnglish'
  | 'ratingStandardEnglish'
  | 'ratingBulletBrazilian'
  | 'ratingBlitzBrazilian'
  | 'ratingRapidBrazilian'
  | 'ratingStandardBrazilian';

/**
 * Map a time control preset string to its category
 */
function getTimeControlCategory(preset: string): 'bullet' | 'blitz' | 'rapid' | 'classical' | null {
  if (preset === 'none') return null;
  if (preset.startsWith('bullet')) return 'bullet';
  if (preset.startsWith('blitz')) return 'blitz';
  if (preset.startsWith('rapid')) return 'rapid';
  if (preset.startsWith('classical')) return 'classical';
  return null;
}

/**
 * Get the User field name for a given variant + time control combo.
 * Returns null if the combo can't be mapped (e.g. 'none' time control).
 */
export function getRatingFieldName(variant: string, timeControl: string): RatingFieldName | null {
  const tc = getTimeControlCategory(timeControl);
  if (!tc) return null;

  const isItalian = variant === 'italian' || variant === 'ITALIAN';
  const isEnglish = variant === 'english' || variant === 'ENGLISH';
  const isBrazilian = variant === 'brazilian' || variant === 'BRAZILIAN';
  // DB uses 'standard' for the 'classical' category
  const tcName = tc === 'classical' ? 'Standard' : tc.charAt(0).toUpperCase() + tc.slice(1);
  const suffix = isItalian ? 'Italian' : isEnglish ? 'English' : isBrazilian ? 'Brazilian' : '';

  return `rating${tcName}${suffix}` as RatingFieldName;
}

/**
 * Get the rating for a specific game variant + time control from a User object.
 * Returns undefined if user is null, the combo can't be mapped, or the user has
 * no rating at all — a guest, whose session carries no rating fields. There is
 * deliberately no default: an invented 1000 is exactly what a guest must not be
 * shown, and callers already render nothing when this is absent.
 */
export function getRatingForGame(
  user: User | null,
  variant: string,
  timeControl: string
): number | undefined {
  if (!user) return undefined;

  const field = getRatingFieldName(variant, timeControl);
  if (!field) return undefined;

  return user[field];
}

/**
 * Vero solo per un punteggio che si può mostrare.
 *
 * Scarta l'assenza (`null`/`undefined`, che il server manda apposta per ospiti,
 * bot, IA locale e account cancellati) e lo zero, che un punteggio non è mai: il
 * vincolo in DB è `CHECK (rating BETWEEN 100 AND 3500)`, quindi uno 0 a schermo
 * è sempre un numero inventato da un `?? 0` o da un `|| 0` a monte.
 *
 * Sta qui, e non in una catena scritta a mano in ogni riquadro, perché la stessa
 * svista è già ricomparsa tre volte in tre schermate diverse.
 */
export function isDisplayableRating(rating: number | null | undefined): rating is number {
  return typeof rating === 'number' && Number.isFinite(rating) && rating > 0;
}

/**
 * Get the rating class based on rating value
 * Matches the server-side Glicko-2 rating classes
 */
export function getRatingClass(rating: number): string {
  if (rating >= 2200) return 'Legend';
  if (rating >= 2000) return 'Grandmaster';
  if (rating >= 1800) return 'Master';
  if (rating >= 1600) return 'Expert';
  if (rating >= 1400) return 'Advanced';
  if (rating >= 1200) return 'Intermediate';
  return 'Novice';
}
